import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UserProfileApi, type Gender } from "@/openapi/generated";
import type { StudentRegistrationFormSchemaType } from "@/schemas";
import { dateValueToString } from "@/components/shared/formElements/DatePicker";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { utcNoonFromYmd } from "@/lib/utils/date/date-only";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";

export const useStudentRegister = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
  userId: number,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (formData: StudentRegistrationFormSchemaType) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(UserProfileApi, accessToken);

      const dateOfBirthYmd = dateValueToString(formData.birthday);
      if (!dateOfBirthYmd) {
        throw new Error("Date of birth is required");
      }
      const dateOfBirth = utcNoonFromYmd(dateOfBirthYmd);

      return service.apiUserProfileIdStudentsRegisterPost({
        id: userId,
        createStudentRequest: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          preferredName: formData.preferredName || null,
          dateOfBirth,
          gender: formData.gender as Gender,
          selfEnrollment: false,
          parentId: userId,
          cellPhone: formData.cellPhone || null,
          homePhone: formData.homePhone || null,
          email: formData.email || null,
          address: formData.address || null,
          locationIds: (formData.locationIds || []).map(Number),
          medications: formData.medications || null,
          allergies: formData.allergies || null,
          specialNeeds: formData.specialNeeds || null,
          postPublicly: formData.postPublicly ?? false,
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UserStudentsGet],
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);

      toast({
        title: "Student registered",
        description: "Student has been successfully registered!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to register student";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
